'use client';

import dynamic from 'next/dynamic';
import * as React from 'react';

import { Textarea } from './textarea';
import { cn } from '@eliadmin/core/src/utils/style-utils';

export type TextareaCkeditorProps = {
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  minHeight?: number;
  maxHeight?: number;
  className?: string;
  'aria-invalid'?: boolean;
};

const stripHtml = (value: string) => value.replace(/<[^>]*>/g, '');

const TextareaCkeditorClient = dynamic(
  () => import('./textarea-ckeditor-client').then(mod => mod.TextareaCkeditorClient),
  {
    ssr: false,
    loading: () => <Textarea readOnly variant="outlined" disabled className="bg-white" rows={6} />,
  },
);

export const TextareaCkeditor = React.forwardRef<HTMLDivElement, TextareaCkeditorProps>(
  (
    { value = '', disabled, placeholder, className, 'aria-invalid': ariaInvalid, ...props },
    ref,
  ) => {
    const [isMounted, setIsMounted] = React.useState(false);

    React.useEffect(() => {
      setIsMounted(true);
    }, []);

    if (!isMounted) {
      return (
        <Textarea
          value={stripHtml(value)}
          readOnly
          variant="outlined"
          disabled={disabled}
          placeholder={placeholder}
          aria-invalid={ariaInvalid}
          className={cn('bg-white', className)}
          rows={6}
        />
      );
    }

    return (
      <TextareaCkeditorClient
        {...props}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        className={className}
        aria-invalid={ariaInvalid}
        textareaRef={ref}
      />
    );
  },
);

TextareaCkeditor.displayName = 'TextareaCkeditor';
